import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";
import { useToast } from "../lib/toast";
import type { EditSequence, VideoAsset, Scene } from "@flowgen/shared";

export function EditTab() {
  const edits = useResource<EditSequence[]>(() => api.get("/edits"), []);
  const videos = useResource<VideoAsset[]>(() => api.get("/videos"), []);
  const scenes = useResource<Scene[]>(() => api.get("/scenes"), []);
  const toast = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [matchBusy, setMatchBusy] = useState(false);

  useEffect(() => {
    if (!edits.data) return;
    if (!activeId && edits.data.length > 0) setActiveId(edits.data[0].id);
  }, [edits.data, activeId]);

  const active = useMemo(
    () => edits.data?.find((e) => e.id === activeId) ?? null,
    [edits.data, activeId]
  );

  const orderedVideos = useMemo(() => {
    if (!active || !videos.data) return [] as VideoAsset[];
    const byId = new Map(videos.data.map((v) => [v.id, v]));
    return active.orderedVideoIds.map((id) => byId.get(id)).filter(Boolean) as VideoAsset[];
  }, [active, videos.data]);

  async function createSequence(name: string) {
    try {
      const res = await api.post<EditSequence>("/edits", { name, orderedVideoIds: [] });
      await edits.reload();
      setActiveId(res.id);
      setCreating(false);
    } catch (err: any) {
      toast.show(err?.message ?? "Failed", "error");
    }
  }

  async function persistOrder(orderedIds: string[]) {
    if (!active) return;
    try {
      await api.patch(`/edits/${active.id}`, { orderedVideoIds: orderedIds });
      edits.reload();
    } catch (err: any) {
      toast.show(err?.message ?? "Failed to save order", "error");
    }
  }

  async function rename(next: string) {
    if (!active) return;
    if (!next.trim()) return;
    try {
      await api.patch(`/edits/${active.id}`, { name: next.trim() });
      edits.reload();
    } catch (err: any) {
      toast.show(err?.message ?? "Failed", "error");
    }
  }

  function move(id: string, direction: -1 | 1) {
    if (!active) return;
    const ids = [...active.orderedVideoIds];
    const idx = ids.indexOf(id);
    if (idx === -1) return;
    const target = idx + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    persistOrder(ids);
  }

  function appendAvailable(videoId: string) {
    if (!active) return;
    if (active.orderedVideoIds.includes(videoId)) return;
    persistOrder([...active.orderedVideoIds, videoId]);
  }

  function removeFromSequence(videoId: string) {
    if (!active) return;
    persistOrder(active.orderedVideoIds.filter((id) => id !== videoId));
  }

  function onDragStart(e: React.DragEvent, videoId: string) {
    e.dataTransfer.setData("video-id", videoId);
    e.dataTransfer.effectAllowed = "move";
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  function onDrop(e: React.DragEvent, beforeId?: string) {
    e.preventDefault();
    e.stopPropagation();
    if (!active) return;
    const id = e.dataTransfer.getData("video-id");
    if (!id) return;
    const ids = active.orderedVideoIds.filter((x) => x !== id);
    if (!beforeId) {
      ids.push(id);
    } else {
      const targetIdx = ids.indexOf(beforeId);
      ids.splice(targetIdx === -1 ? ids.length : targetIdx, 0, id);
    }
    persistOrder(ids);
  }

  async function matchCut(pred: VideoAsset, succ: VideoAsset) {
    setMatchBusy(true);
    try {
      const res = await api.post<{
        tailTrimSeconds: number;
        headTrimSeconds: number;
        alreadyAligned: boolean;
      }>("/scenes/match-cut", {
        predecessorSceneId: pred.sceneId,
        successorSceneId: succ.sceneId,
      });
      videos.reload({ silent: true });
      scenes.reload({ silent: true });
      if (res.alreadyAligned) {
        toast.show("Junction is already frame-matched", "success");
      } else {
        const bits: string[] = [];
        if (res.tailTrimSeconds > 0) bits.push(`${res.tailTrimSeconds.toFixed(2)}s tail`);
        if (res.headTrimSeconds > 0) bits.push(`${res.headTrimSeconds.toFixed(2)}s head`);
        toast.show(`Match cut: trimmed ${bits.join(" + ")}`, "success");
      }
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Match cut failed", "error");
    } finally {
      setMatchBusy(false);
    }
  }

  async function undoJunction(pred: VideoAsset, succ: VideoAsset) {
    setMatchBusy(true);
    try {
      for (const v of [pred, succ]) {
        const scene = scenes.data?.find((s) => s.id === v.sceneId);
        if (scene?.originalVideoUrl) {
          await api.post(`/scenes/${scene.id}/undo-trim`);
        }
      }
      videos.reload({ silent: true });
      scenes.reload({ silent: true });
      toast.show("Junction trims undone", "success");
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Undo failed", "error");
    } finally {
      setMatchBusy(false);
    }
  }

  return (
    <div>
      <div className="workspace-header">
        <div>
          <h2>Edit</h2>
          <div className="sub">Arrange generated clips in order — top of the list plays first.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="primary" onClick={() => setCreating(true)}>New sequence</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 24 }}>
        <div>
          <div className="section-title tiny muted" style={{ marginBottom: 6 }}>Sequences</div>
          {edits.data && edits.data.length === 0 && (
            <div className="empty">No sequences yet.</div>
          )}
          {edits.data?.map((e) => (
            <button
              key={e.id}
              className={`tab-btn ${e.id === activeId ? "active" : ""}`}
              onClick={() => setActiveId(e.id)}
              style={{ marginBottom: 4 }}
            >
              <span style={{ flex: 1 }}>{e.name}</span>
              <span className="tiny muted">{e.orderedVideoIds.length}</span>
            </button>
          ))}
        </div>

        <div>
          {!active && (
            <div className="empty">Select or create a sequence.</div>
          )}
          {active && (
            <div className="card" style={{ padding: 16 }}>
              <div className="row between">
                <input
                  defaultValue={active.name}
                  key={active.id + "-" + active.name}
                  onBlur={(e) => rename(e.target.value)}
                  style={{ maxWidth: 320 }}
                />
                <div className="row" style={{ gap: 8 }}>
                  <button
                    disabled={orderedVideos.length === 0}
                    onClick={async () => {
                      try {
                        const res = await api.post<EditSequence>(`/edits/${active.id}/export`);
                        toast.show("Exported master MP4", "success");
                        edits.reload();
                        if (res.exportUrl) {
                          window.open(api.mediaUrl(res.exportUrl), "_blank");
                        }
                      } catch (err: any) {
                        toast.show(err?.details?.message ?? err?.message ?? "Export failed", "error");
                      }
                    }}
                  >
                    Export MP4
                  </button>
                  <button className="danger" onClick={async () => {
                    if (!confirm("Delete sequence?")) return;
                    await api.delete(`/edits/${active.id}`);
                    setActiveId(null);
                    edits.reload();
                  }}>Delete</button>
                </div>
              </div>
              {active.exportUrl && (
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  Last export:{" "}
                  <a href={api.mediaUrl(active.exportUrl)} target="_blank" rel="noreferrer">
                    {active.exportUrl}
                  </a>
                </div>
              )}

              <div className="spacer-12"></div>

              {orderedVideos.length > 0 && (
                <SequencePlayer
                  key={active.id + "-" + orderedVideos.map((v) => v.id).join(",")}
                  clips={orderedVideos}
                  scenes={scenes.data ?? []}
                />
              )}

              <div className="spacer-12"></div>

              <div
                className="editor-list"
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e)}
              >
                {orderedVideos.length === 0 ? (
                  <div className="empty" style={{ minHeight: 120 }}>
                    Drag videos from the tray below into this sequence.
                  </div>
                ) : (
                  orderedVideos.map((v, i) => {
                    const scene = scenes.data?.find((s) => s.id === v.sceneId);
                    const next = orderedVideos[i + 1];
                    const nextScene = next
                      ? scenes.data?.find((s) => s.id === next.sceneId)
                      : null;
                    const isContinuityJunction =
                      !!scene &&
                      !!nextScene &&
                      !!nextScene.continueEnabled &&
                      nextScene.continuesFromSceneId === scene.id;
                    const junctionTrimmed =
                      !!scene?.originalVideoUrl || !!nextScene?.originalVideoUrl;
                    const trimBits: string[] = [];
                    if (scene?.trimStartSeconds) trimBits.push(`head −${scene.trimStartSeconds.toFixed(2)}s`);
                    if (scene?.trimEndSeconds) trimBits.push(`tail −${scene.trimEndSeconds.toFixed(2)}s`);
                    return (
                      <div key={v.id}>
                        <div
                          className="editor-row"
                          draggable
                          onDragStart={(e) => onDragStart(e, v.id)}
                          onDragOver={onDragOver}
                          onDrop={(e) => onDrop(e, orderedVideos[i + 1]?.id)}
                        >
                          <video
                            src={api.mediaUrl(v.url)}
                            muted
                            playsInline
                            preload="metadata"
                            draggable={false}
                          />
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {i + 1}. {scene?.title ?? "Untitled"}
                            </div>
                            <div className="tiny muted">
                              {v.model} · {Math.round(v.durationSeconds)}s
                              {trimBits.length ? ` · cut ${trimBits.join(", ")}` : ""}
                            </div>
                          </div>
                          <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                            <button onClick={() => move(v.id, -1)} disabled={i === 0}>↑</button>
                            <button onClick={() => move(v.id, 1)} disabled={i === orderedVideos.length - 1}>↓</button>
                            <button className="danger" onClick={() => removeFromSequence(v.id)}>×</button>
                          </div>
                        </div>
                        {isContinuityJunction && next && (
                          <div className="junction-row">
                            <span className="junction-line" />
                            <span className="tiny muted">
                              {junctionTrimmed ? "Match-cut applied" : "Continue-from junction"}
                            </span>
                            <button
                              disabled={matchBusy}
                              onClick={() => matchCut(v, next)}
                            >
                              {matchBusy ? "Matching…" : "Re-match"}
                            </button>
                            {junctionTrimmed && (
                              <button disabled={matchBusy} onClick={() => undoJunction(v, next)}>
                                Undo trims
                              </button>
                            )}
                            <span className="junction-line" />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="spacer-24"></div>
              <div className="section-title tiny muted" style={{ marginBottom: 6 }}>Available clips</div>
              {(!videos.data || videos.data.length === 0) && (
                <div className="empty">No videos yet. Generate scenes to populate this tray.</div>
              )}
              {videos.data && videos.data.length > 0 && (
                <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
                  {videos.data
                    .filter((v) => !active.orderedVideoIds.includes(v.id))
                    .map((v) => {
                      const scene = scenes.data?.find((s) => s.id === v.sceneId);
                      return (
                        <div
                          key={v.id}
                          className="card"
                          draggable
                          onDragStart={(e) => onDragStart(e, v.id)}
                          onClick={() => appendAvailable(v.id)}
                          style={{ cursor: "grab" }}
                        >
                          <video
                            src={api.mediaUrl(v.url)}
                            muted
                            playsInline
                            preload="metadata"
                            draggable={false}
                            style={{ pointerEvents: "none" }}
                          />
                          <div style={{ fontWeight: 600 }}>{scene?.title ?? "Untitled"}</div>
                          <div className="tiny muted">{v.model} · {Math.round(v.durationSeconds)}s</div>
                          <div className="tiny muted">Drag into sequence, or click to append</div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {creating && <NameDialog onCancel={() => setCreating(false)} onConfirm={createSequence} />}
        </div>
      </div>
    </div>
  );
}

function SequencePlayer({
  clips,
  scenes,
}: {
  clips: VideoAsset[];
  scenes: Scene[];
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const indexRef = useRef(0);
  indexRef.current = index;

  const clip = clips[index];
  const scene = clip ? scenes.find((s) => s.id === clip.sceneId) : null;
  const src = clip ? api.mediaUrl(clip.url) : "";

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return;
    el.load();
    if (playing) {
      el.play().catch(() => setPlaying(false));
    }
  }, [src, index]);

  function playFrom(i: number) {
    setIndex(i);
    setPlaying(true);
    // play happens in effect after src swap
    requestAnimationFrame(() => {
      videoRef.current?.play().catch(() => setPlaying(false));
    });
  }

  function togglePlay() {
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    el.play().catch(() => setPlaying(false));
  }

  function onEnded() {
    const next = indexRef.current + 1;
    if (next >= clips.length) {
      setPlaying(false);
      return;
    }
    setIndex(next);
    setPlaying(true);
  }

  const totalSec = clips.reduce((n, c) => n + (c.durationSeconds || 0), 0);

  return (
    <div className="sequence-player">
      <div className="row between" style={{ marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Sequence playback</div>
          <div className="tiny muted">
            Clip {index + 1} of {clips.length}
            {scene ? ` · ${scene.title}` : ""}
            {" · "}
            ~{Math.round(totalSec)}s total
          </div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button onClick={() => playFrom(0)} disabled={clips.length === 0}>
            Restart
          </button>
          <button className="primary" onClick={togglePlay} disabled={!clip}>
            {playing ? "Pause" : "Play through"}
          </button>
        </div>
      </div>

      <video
        ref={videoRef}
        className="sequence-player-video"
        src={src}
        controls
        playsInline
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={onEnded}
      />

      <div className="sequence-clip-dots" style={{ marginTop: 8 }}>
        {clips.map((c, i) => {
          const title = scenes.find((s) => s.id === c.sceneId)?.title ?? `Clip ${i + 1}`;
          return (
            <button
              key={c.id}
              type="button"
              className={`sequence-clip-dot ${i === index ? "active" : ""}`}
              title={title}
              onClick={() => playFrom(i)}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NameDialog({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (n: string) => void }) {
  const [v, setV] = useState("");
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && v.trim()) onConfirm(v);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [v, onCancel, onConfirm]);
  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <div className="dialog" style={{ width: 400 }} onClick={(e) => e.stopPropagation()}>
        <h3>New sequence</h3>
        <div className="field">
          <label>Name</label>
          <input value={v} onChange={(e) => setV(e.target.value)} placeholder="My sequence" />
        </div>
        <div className="row between">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" disabled={!v.trim()} onClick={() => onConfirm(v)}>Create</button>
        </div>
      </div>
    </div>
  );
}
